import { SolAuthority } from '@sol/components';
import { AsmAuthority } from '../models/asm-authority';
import { AsmLicensingAuthority } from '../models/asm-licensing-authority';

export interface AsmLicensingIdentity {
  issuer: string;
  subject: string;
}

export class AsmLicensingAuthorizationTools {
  private static readonly USER = 'license_service_user';
  private static readonly EDITOR = 'license_service_editor';
  private static readonly PRODUCT_MANAGER = 'license_service_product_manager';
  private static readonly GROUPS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
    dit: {
      '5538de55-f3c0-4cda-b415-e822853bca78': AsmLicensingAuthorizationTools.USER,
      'f2ee08e3-d980-400c-aeb0-6d3e227776d5': AsmLicensingAuthorizationTools.EDITOR,
      'aa0df5d0-bf61-456e-aed2-02f81044b9e9': AsmLicensingAuthorizationTools.PRODUCT_MANAGER,
    },
    uat: {
      '5223a9f1-317d-42cd-8df0-eb015ca54bf7': AsmLicensingAuthorizationTools.USER,
      '850059ed-5851-4da0-887f-b5545c69e781': AsmLicensingAuthorizationTools.EDITOR,
      '326dee3a-d2bd-4337-aa91-810c96401751': AsmLicensingAuthorizationTools.PRODUCT_MANAGER,
    },
    prod: {
      '4f1ebdde-824e-46d7-9584-d224dd9cc844': AsmLicensingAuthorizationTools.USER,
      '8ee9648d-470a-4c36-9df6-49e0d8ddc894': AsmLicensingAuthorizationTools.EDITOR,
      'fea8f1f3-0c52-47fc-80cf-6bae123bd32f': AsmLicensingAuthorizationTools.PRODUCT_MANAGER,
    },
    sandbox: {
      'sandbox-licensing-user': AsmLicensingAuthorizationTools.USER,
      'sandbox-licensing-editor': AsmLicensingAuthorizationTools.EDITOR,
      'sandbox-licensing-product-manager': AsmLicensingAuthorizationTools.PRODUCT_MANAGER,
    },
  };

  private constructor() {}

  public static toAuthorities(token: string | undefined, environment: string): SolAuthority[] {
    const roles = this.toRoles(token, environment);
    const canManageCatalog = roles.includes(this.PRODUCT_MANAGER);
    const canManageCustomers = canManageCatalog || roles.includes(this.EDITOR);
    const canView = canManageCustomers || roles.includes(this.USER);
    return [
      ...(canView ? [AsmLicensingAuthority.VIEW_APP, AsmLicensingAuthority.VIEW] : []),
      ...(canManageCustomers ? [AsmLicensingAuthority.MANAGE_CUSTOMERS] : []),
      ...(canManageCatalog ? [AsmLicensingAuthority.MANAGE_CATALOG] : []),
    ];
  }

  public static permissionsToAuthorities(permissions: readonly string[]): SolAuthority[] {
    const canEdit = permissions.includes(AsmAuthority.EDIT_ADMIN_LICENSING);
    const canView = canEdit || permissions.includes(AsmAuthority.VIEW_ADMIN_LICENSING);
    return [
      ...(canView ? [AsmLicensingAuthority.VIEW_APP, AsmLicensingAuthority.VIEW] : []),
      ...(canEdit ? [AsmLicensingAuthority.MANAGE_CUSTOMERS, AsmLicensingAuthority.MANAGE_CATALOG] : []),
    ];
  }

  public static toIdentity(token: string | undefined): AsmLicensingIdentity | undefined {
    const claims = this.toClaims(token);
    const issuer = claims?.['iss'];
    const subject = claims?.['sub'];
    return typeof issuer === 'string' && issuer.length > 0 && typeof subject === 'string' && subject.length > 0
      ? { issuer, subject }
      : undefined;
  }

  public static isAvailable(url: string | undefined): boolean {
    if (!url || url.includes('TBD')) {
      return false;
    }
    try {
      return ['http:', 'https:'].includes(new URL(url).protocol);
    } catch {
      return false;
    }
  }

  private static toRoles(token: string | undefined, environment: string): string[] {
    const claims = this.toClaims(token);
    if (!claims) {
      return [];
    }
    const realm = claims['realm_access'];
    const clients = claims['resource_access'];
    const mappings = this.GROUPS[environment] ?? {};
    return [
      ...(this.isRecord(realm) ? this.toStrings(realm['roles']) : []),
      ...(this.isRecord(clients)
        ? Object.values(clients).flatMap(client => (this.isRecord(client) ? this.toStrings(client['roles']) : []))
        : []),
      ...this.toStrings(claims['idp_groups']).flatMap(group => (mappings[group] ? [mappings[group]] : [])),
    ];
  }

  private static toClaims(token: string | undefined): Record<string, unknown> | undefined {
    if (!token) {
      return undefined;
    }
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return undefined;
      }
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const bytes = Uint8Array.from(atob(payload), char => char.charCodeAt(0));
      const claims: unknown = JSON.parse(new TextDecoder().decode(bytes));
      return this.isRecord(claims) ? claims : undefined;
    } catch {
      return undefined;
    }
  }

  private static toStrings(value: unknown): string[] {
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
  }

  private static isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }
}
